import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import html2pdf from 'html2pdf.js';
import { ArrowLeft, Download, BookOpen, Loader2 } from 'lucide-react';

const MarkdownBody = React.memo(function MarkdownBody({ markdownContent, components }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {markdownContent}
    </ReactMarkdown>
  );
});

export default function UserManual() {
  const navigate = useNavigate();
  const [markdownContent, setMarkdownContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const contentRef = useRef(null);
  const handleBackHome = useCallback(() => {
    // 如果正在导出 PDF，主线程很可能被 html2canvas 占用，禁用返回以避免“点了没反应/卡住”的体验
    if (isExporting) return;
    if (isLeaving) return;

    setIsLeaving(true);

    // 直接硬跳转回主页，避免 SPA 卸载大 DOM / 触发权限检查造成卡顿
    window.location.assign('/');

    // 兜底：若未跳转，再尝试一次
    window.setTimeout(() => {
      if (window.location.pathname === '/manual') {
        window.location.href = '/';
      }
    }, 300);
  }, [isExporting, isLeaving]);

  const headingIndexRef = useRef([]); // [{ id, top }]
  const rafRef = useRef(0);
  const lastActiveRef = useRef('');

  // 清理手册中的纯展示 HTML 包裹（例如 <div align="center"> ... </div>），避免在页面/导出的 PDF 中显示原始标签
  const sanitizeManualMarkdown = (rawText) => {
    if (!rawText) return '';

    const lines = rawText.split('\n');
    const out = [];

    const isOpenCenterDiv = (line) =>
      /^\s*<div\b[^>]*\balign\s*=\s*["']?center["']?[^>]*>\s*$/i.test(line.trim());
    const isCloseDiv = (line) => /^\s*<\/div>\s*$/i.test(line.trim());

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (isOpenCenterDiv(line)) continue;
      if (isCloseDiv(line)) continue;
      out.push(line);
    }

    // 清掉首尾多余空行
    return out.join('\n').replace(/^\s*\n+/, '').replace(/\n+\s*$/, '\n');
  };

  // 加载 Markdown 文件
  useEffect(() => {
    fetch('/USER_MANUAL.md')
      .then((response) => {
        if (!response.ok) {
          throw new Error('无法加载用户手册');
        }
        return response.text();
      })
      .then((text) => {
        setMarkdownContent(sanitizeManualMarkdown(text));
        setLoading(false);
      })
      .catch((err) => {
        console.error('加载用户手册失败:', err);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // 生成标题 ID 的辅助函数
  const generateId = useCallback((text) => {
    // 处理可能是数组或对象的情况
    let textStr = '';
    if (typeof text === 'string') {
      textStr = text;
    } else if (Array.isArray(text)) {
      textStr = text.map(item => typeof item === 'string' ? item : '').join('');
    } else {
      textStr = String(text);
    }
    
    // 移除 emoji 和多余空格，但保留中文字符
    return textStr
      .replace(/[📑📖🎯🚀🏁📝🔧👨‍💼❓🛠️📌📄✅📚📊👥📅✨📱🏗️🔐💡]/g, '')
      .trim()
      .replace(/\s+/g, '')  // 移除所有空格
      .toLowerCase();
  }, []);

  // 生成目录
  const toc = useMemo(() => {
    if (!markdownContent) return [];
    
    const lines = markdownContent.split('\n');
    const toc = [];
    
    lines.forEach((line) => {
      const h2Match = line.match(/^##\s+(.+)$/);
      const h3Match = line.match(/^###\s+(.+)$/);
      
      if (h2Match) {
        const title = h2Match[1].replace(/[📑📖🎯🚀🏁📝🔧👨‍💼❓🛠️📌📄✅📚📊👥📅✨📱🏗️🔐💡]/g, '').trim();
        const id = generateId(title);
        toc.push({ level: 2, title, id });
      } else if (h3Match) {
        const title = h3Match[1].replace(/[📑📖🎯🚀🏁📝🔧👨‍💼❓🛠️📌📄✅📚📊👥📅✨📱🏗️🔐💡]/g, '').trim();
        const id = generateId(title);
        toc.push({ level: 3, title, id });
      }
    });
    
    return toc;
  }, [markdownContent, generateId]);

  // 预计算标题在容器内的位置，并用 rAF 节流滚动更新（避免每次 scroll 都 query + setState）
  useEffect(() => {
    const contentElement = contentRef.current;
    if (contentElement) {
      // 预计算：只做一次 DOM 查询
      const headings = Array.from(contentElement.querySelectorAll('h2[id], h3[id]'));
      headingIndexRef.current = headings
        .map((h) => ({ id: h.id, top: h.offsetTop }))
        .sort((a, b) => a.top - b.top);

      const updateActive = () => {
        rafRef.current = 0;
        const scrollTop = contentElement.scrollTop;
        const threshold = scrollTop + 120; // 顶部偏移，和 UI 顶部留白一致
        const idx = headingIndexRef.current;
        if (!idx.length) return;

        // 找到最后一个 top <= threshold 的标题
        let lo = 0;
        let hi = idx.length - 1;
        let best = 0;
        while (lo <= hi) {
          const mid = (lo + hi) >> 1;
          if (idx[mid].top <= threshold) {
            best = mid;
            lo = mid + 1;
          } else {
            hi = mid - 1;
          }
        }

        const nextId = idx[best]?.id || '';
        if (nextId && nextId !== lastActiveRef.current) {
          lastActiveRef.current = nextId;
          setActiveSection(nextId);
        }
      };

      const onScroll = () => {
        if (rafRef.current) return;
        rafRef.current = window.requestAnimationFrame(updateActive);
      };

      // 初始化一次
      onScroll();

      contentElement.addEventListener('scroll', onScroll, { passive: true });
      return () => {
        contentElement.removeEventListener('scroll', onScroll);
        if (rafRef.current) {
          window.cancelAnimationFrame(rafRef.current);
          rafRef.current = 0;
        }
      };
    }
  }, [markdownContent]);

  // 跳转到指定章节
  const scrollToSection = useCallback((id) => {
    const element = document.getElementById(id);
    if (element && contentRef.current) {
      const container = contentRef.current;
      const elementTop = element.offsetTop;
      container.scrollTo({
        top: elementTop - 100,
        behavior: 'smooth'
      });
    }
  }, []);

  // 导出为PDF
  const exportToPDF = async () => {
    if (!contentRef.current) return;

    setIsExporting(true);
    const element = contentRef.current;
    // 保存原始样式（无论成功/失败都要恢复，避免页面布局异常导致“无响应”）
    const originalStyles = {
      maxHeight: element.style.maxHeight,
      overflow: element.style.overflow,
      height: element.style.height,
      position: element.style.position,
      top: element.style.top,
      left: element.style.left,
      zIndex: element.style.zIndex,
    };

    try {
      // 临时移除高度限制，使所有内容可见
      element.style.maxHeight = 'none';
      element.style.overflow = 'visible';
      element.style.height = 'auto';
      element.style.position = 'relative';
      element.style.top = 'auto';
      element.style.left = 'auto';
      element.style.zIndex = 'auto';
      
      // 滚动到顶部
      element.scrollTop = 0;
      
      // 等待样式应用和内容渲染
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const opt = {
        margin: [10, 10, 10, 10],
        filename: 'Magic-Page-用户手册.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          logging: false,
          windowWidth: element.scrollWidth || 794,
          windowHeight: element.scrollHeight || 1123,
          allowTaint: true,
          backgroundColor: '#ffffff',
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
          compress: true,
        },
        pagebreak: { 
          mode: ['avoid-all', 'css', 'legacy'],
          avoid: ['h2', 'h3', 'img', 'table', 'pre'],
        },
      };

      await html2pdf().set(opt).from(element).save();
    } catch (err) {
      console.error('PDF导出失败:', err);
      alert('PDF导出失败，请稍后重试。如果问题持续，请尝试刷新页面后重试。');
    } finally {
      // 无论成功/失败都恢复原始样式，避免影响页面交互
      element.style.maxHeight = originalStyles.maxHeight;
      element.style.overflow = originalStyles.overflow;
      element.style.height = originalStyles.height;
      element.style.position = originalStyles.position;
      element.style.top = originalStyles.top;
      element.style.left = originalStyles.left;
      element.style.zIndex = originalStyles.zIndex;
      setIsExporting(false);
    }
  };

  // 自定义 Markdown 组件，为标题添加 ID
  const components = useMemo(() => ({
    h2: ({ children, ...props }) => {
      const id = generateId(children);
      return <h2 id={id} {...props}>{children}</h2>;
    },
    h3: ({ children, ...props }) => {
      const id = generateId(children);
      return <h3 id={id} {...props}>{children}</h3>;
    },
  }), [generateId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">正在加载用户手册...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">加载失败</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            返回主页
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航栏 */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBackHome}
                disabled={isExporting || isLeaving}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium">{isLeaving ? '正在返回...' : '返回主页'}</span>
              </button>
              <div className="hidden sm:flex items-center space-x-2 text-gray-400">
                <span>/</span>
                <BookOpen className="w-5 h-5" />
                <span className="font-medium">用户使用手册</span>
              </div>
            </div>
            <button
              onClick={exportToPDF}
              disabled={isExporting}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="hidden sm:inline">导出中...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">下载PDF</span>
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* 主内容区域 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* 左侧目录 */}
          <aside className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border p-6 sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <BookOpen className="w-5 h-5 mr-2 text-blue-600" />
                目录
              </h3>
              <nav className="space-y-1">
                {toc.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      item.level === 2 ? 'font-medium' : 'text-sm pl-6'
                    } ${
                      activeSection === item.id
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {item.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* 右侧内容 */}
          <main className="lg:col-span-3">
            <div
              ref={contentRef}
              className="bg-white rounded-lg shadow-sm border p-8 prose prose-slate max-w-none overflow-y-auto"
              style={{ maxHeight: 'calc(100vh - 8rem)' }}
            >
              <MarkdownBody markdownContent={markdownContent} components={components} />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
